import type { InputTypes, UploadWithRefTypes, UploadTypes } from '@/parsers'
import { getTransformer } from '@/parsers'

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import type { FCWithChildren, Reference } from '@/types'
import { booleanFilterTypeGuard } from '@/util/helpers'
import type { UploadTableName } from './types'

// Import services
import {
  FileProcessingService,
  type FileProcessingResult,
} from './services/FileProcessingService'
import {
  SpeciesValidationService,
  type SpeciesValidationResult,
} from './services/SpeciesValidationService'
import {
  ReferenceService,
  type NewReference,
} from './services/ReferenceService'
import { UploadService, type UploadResult } from './services/UploadService'

// Simplified UI Stage enum for user experience
export enum UIStage {
  READY = 'READY', // Show file drop or upload preview
  PROCESSING = 'PROCESSING', // Show loading spinner (any processing/uploading)
  SUCCESS = 'SUCCESS', // Show success message
  FILE_ERROR = 'FILE_ERROR', // Show file error message
  ERROR = 'ERROR', // Show error message
}

// Simplified state interface
interface UploadState {
  // UI Stage for component rendering
  stage: UIStage

  // Data
  inputData?: InputTypes[]
  uploadData?: UploadWithRefTypes[]

  // Results
  successRows?: number

  // Errors
  fileError?: string
  validationErrors?: Record<string, string>

  // Settings
  dryRun: boolean
}

// Orchestrator hook
export const useUploadOrchestrator = () => {
  const [state, setState] = useState<UploadState>({
    stage: UIStage.READY,
    dryRun: false,
  })

  // Services
  const speciesService = useMemo(() => new SpeciesValidationService(), [])
  const referenceService = useMemo(() => new ReferenceService(), [])
  const uploadService = useMemo(() => new UploadService(), [])

  const updateState = useCallback((updates: Partial<UploadState>) => {
    setState((prev) => ({ ...prev, ...updates }))
  }, [])

  const setDryRun = useCallback(
    (dryRun: boolean) => {
      updateState({ dryRun })
    },
    [updateState],
  )

  const handleReset = useCallback(() => {
    setState({
      stage: UIStage.READY,
      dryRun: false,
    })
  }, [])

  const processAndValidateInputRow = useCallback(
    async (
      inputRow: InputTypes,
      rowIndex: number,
      tableName: UploadTableName,
    ): Promise<UploadWithRefTypes | null> => {
      const {
        taxon_name,
        new_reference_citation,
        new_reference_description,
        reference_id,
        ...uploadRow
      } = inputRow

      // Validate taxon name
      const taxonValidation = speciesService.validateTaxonName(taxon_name)
      if (!taxonValidation.isValid) {
        updateState({
          validationErrors: {
            ...state.validationErrors,
            [rowIndex]: taxonValidation.error,
          },
        })
        return null
      }

      // Transform upload row
      const transformer = getTransformer(tableName)
      const transformedUploadRow = transformer(uploadRow)

      const result: UploadTypes & { taxon_name: string } = {
        ...transformedUploadRow,
        taxon_name,
      }

      // Handle reference logic
      if (
        !reference_id &&
        new_reference_citation &&
        new_reference_description
      ) {
        try {
          const existingRefId = await referenceService.checkExistingReference(
            new_reference_citation,
            new_reference_description,
          )

          if (existingRefId) {
            return { ...result, reference_id: existingRefId }
          } else {
            return {
              ...result,
              new_reference_citation,
              new_reference_description,
            }
          }
        } catch (e) {
          updateState({
            validationErrors: {
              ...state.validationErrors,
              [rowIndex]: (e as Error).message,
            },
          })
          return null
        }
      }

      return { ...result, reference_id }
    },
    [speciesService, referenceService, state.validationErrors, updateState],
  )

  const processFile = useCallback(
    async (
      file: File,
    ): Promise<
      | {
          uploadData: UploadWithRefTypes[]
          tableName: UploadTableName
        }
      | undefined
    > => {
      try {
        updateState({
          stage: UIStage.PROCESSING,
          fileError: undefined,
          validationErrors: undefined,
        })

        // Step 1: Process file
        const fileResult: FileProcessingResult =
          await FileProcessingService.processFile(file)

        if (Object.keys(fileResult.errors).length > 0) {
          updateState({
            stage: UIStage.ERROR,
            validationErrors: fileResult.errors,
            fileError: 'File contains validation errors',
          })
          return
        }

        updateState({
          inputData: fileResult.inputData,
        })

        // Step 2: Validate and transform input data
        const uploadRowsWithTaxonNames = await Promise.all(
          fileResult.inputData.map(
            async (dataRow, i) =>
              await processAndValidateInputRow(
                dataRow,
                i,
                fileResult.tableName,
              ),
          ),
        )

        const validUploadRows = uploadRowsWithTaxonNames.filter(
          booleanFilterTypeGuard,
        )

        if (validUploadRows.length === 0) {
          updateState({
            stage: UIStage.ERROR,
            fileError: 'No valid rows found after validation',
          })
          return
        }

        // Step 3: Lookup species
        const uniqueTaxonNames = Array.from(
          new Set(validUploadRows.map(({ taxon_name }) => taxon_name)),
        )

        const speciesResult: SpeciesValidationResult =
          await speciesService.lookupSpecies(uniqueTaxonNames)

        // Check for species lookup errors
        const speciesErrors: Record<string, string> = {}
        let hasSpeciesErrors = false

        // Map species IDs to upload rows
        const finalUploadRows = validUploadRows
          .map((uploadRow, i) => {
            const { taxon_name, ...rest } = uploadRow
            const speciesLookup = speciesResult.speciesLookupMap[taxon_name]

            if (speciesLookup?.error) {
              speciesErrors[i] = speciesLookup.error
              hasSpeciesErrors = true
              return null
            }

            return {
              species_id: speciesLookup?.data,
              ...rest,
            }
          })
          // UploadWithRefTypes type not exactly correct, value inclues { taxon_name: string }
          .filter(booleanFilterTypeGuard) as UploadWithRefTypes[]

        if (hasSpeciesErrors) {
          updateState({
            stage: UIStage.ERROR,
            validationErrors: { ...state.validationErrors, ...speciesErrors },
            fileError: 'Species validation failed',
          })
          return
        }
        // File processed successfully - ready for upload
        updateState({
          stage: UIStage.READY,
          uploadData: finalUploadRows,
        })
        return {
          uploadData: finalUploadRows,
          tableName: fileResult.tableName,
        }
      } catch (error) {
        updateState({
          stage: UIStage.ERROR,
          fileError: (error as Error).message,
        })
      }
    },
    [
      processAndValidateInputRow,
      speciesService,
      state.validationErrors,
      updateState,
    ],
  )

  const executeUpload = useCallback(
    async (
      params:
        | {
            uploadData: UploadWithRefTypes[]
            tableName: UploadTableName
          }
        | undefined,
    ) => {
      if (!params?.uploadData) {
        updateState({
          stage: UIStage.FILE_ERROR,
          fileError: 'No data to upload',
        })
        return
      }
      if (!params.tableName) {
        updateState({
          stage: UIStage.FILE_ERROR,
          fileError: 'Incorrect file name',
        })
        return
      }
      const { uploadData, tableName } = params

      if (state.dryRun) {
        updateState({
          stage: UIStage.SUCCESS,
          successRows: uploadData.length,
        })
        return
      }

      try {
        updateState({ stage: UIStage.PROCESSING })

        // Step 1: Create new references
        const newRefs: NewReference[] = Array.from(
          new Set(
            uploadData
              .map(
                ({
                  new_reference_description: description,
                  new_reference_citation: citation,
                }) =>
                  description && citation ? { description, citation } : null,
              )
              .filter(booleanFilterTypeGuard)
              .map((ref) => JSON.stringify(ref)),
          ),
        ).map((ref) => JSON.parse(ref))

        let newRefsMap: Record<string, Reference> = {}
        if (newRefs.length > 0) {
          newRefsMap = await referenceService.createReferences(newRefs)
        }

        // Step 2: Update upload data with new reference IDs
        const updatedUploadData = uploadData.map((upload) => {
          if (upload.new_reference_description) {
            const newRef = newRefsMap[upload.new_reference_description]
            if (newRef) {
              return { ...upload, reference_id: newRef.id }
            }
          }
          return upload
        })

        // Step 3: Clean upload data (remove reference creation fields)
        const cleanUploadData: UploadTypes[] = updatedUploadData.map(
          (upload) => {
            const {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              new_reference_citation,
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              new_reference_description,
              ...rest
            } = upload
            return rest
          },
        )

        // Step 4: Upload data
        const uploadResult: UploadResult = await uploadService.uploadData(
          tableName,
          cleanUploadData,
        )

        if (uploadResult.errors.length > 0) {
          const errorMessage =
            uploadResult.successCount > 0
              ? `There was an error, but ${uploadResult.successCount} rows were successfully uploaded. ${uploadResult.errors[0]}`
              : `Unable to upload data: ${uploadResult.errors[0]}`
          updateState({
            stage: UIStage.ERROR,
            validationErrors: { 0: errorMessage },
            successRows:
              uploadResult.successCount > 0
                ? uploadResult.successCount
                : undefined,
          })
        } else {
          updateState({
            stage: UIStage.SUCCESS,
            successRows: uploadResult.successCount,
          })
        }
      } catch (error) {
        updateState({
          stage: UIStage.ERROR,
          fileError: (error as Error).message,
        })
      }
    },
    [state.dryRun, referenceService, uploadService, updateState],
  )

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        processFile(acceptedFiles[0]).then((result) => {
          executeUpload(result)
        })
      }
    },
    [processFile, executeUpload],
  )

  const setFileError = useCallback(
    (error: string) => {
      updateState({ fileError: error })
    },
    [updateState],
  )

  return {
    state,
    onDrop,
    executeUpload,
    setDryRun,
    handleReset,
    setFileError,
  }
}

// Simplified context
const defaultContext = {} as unknown as ReturnType<typeof useUploadOrchestrator>
const UploadContext = createContext(defaultContext)

export const UploadProvider: FCWithChildren = ({ children }) => (
  <UploadContext.Provider value={useUploadOrchestrator()}>
    {children}
  </UploadContext.Provider>
)

export const useUpload = () => useContext(UploadContext)
